'use client';

// Package Imports
import React, { useEffect, useState, useRef, useCallback } from 'react';
import Image from 'next/image';
import classNames from 'classnames';

// Type Imports
import { Image as ImageType, Images } from '@/lib/types/products';

// Constants Imports
import { PlaceholderImageGrey, PlaceholderImageWhite } from '@/lib/constants/common';

// Function Imports
import useMousePosition from '@/lib/functions/useMousePosition';

// Style Imports
import styles from './productGallery.module.scss';

export function ProductGallery({
  className,
  title,
  images,
}: {
  className: string;
  title: string;
  images: Images;
}) {
  const [positionStyles, setPositionStyles] = useState({});
  const [selectedImage, setSelectedImage] = useState(images[0]);
  const defaultImageUsed = images[0].id === 'default';
  const hasMultipleImages = images.length > 1;
  const ref = useRef<HTMLDivElement>(null);
  const mouse = useMousePosition(ref);

  const wrapperWidth = useCallback(() => {
    if (ref.current) {
      const { clientWidth } = ref.current;
      return clientWidth;
    }
    return 0;
  }, [ref]);

  useEffect(() => {
    const howMuchToMove = (input: number | null) => {
      if (!input || !ref.current) return { left: "unset" };

      const currentWidth = ref.current ? wrapperWidth() : 416;
      const middle = currentWidth / 2;
      const distanceCanMoveTotal = (500 - currentWidth) / 2;
      const canMove = distanceCanMoveTotal / 2;
      const percentage = Math.floor((input / currentWidth) * 100);
      let output = 0;

      if (input < middle) {
        output = canMove * (1 - (percentage * 2) / 100);
        output = Math.abs(output);
      } else {
        output = canMove * ((1 - percentage) / 100);
        output = -Math.abs(output);
      }

      return { left: `${output}px`, transition: "left .05s ease-in-out" };
    };

    const defaultStyles = {
      left: "unset",
    };

    if (!defaultImageUsed && hasMultipleImages) {
      if (wrapperWidth() > 500 || mouse.x == null) {
        // reset
        setPositionStyles({});
        return;
      }
      const m = howMuchToMove(mouse.x);
      setPositionStyles({ ...defaultStyles, ...m });
    } else {
      setPositionStyles({});
    }
  }, [mouse.x, defaultImageUsed, hasMultipleImages, wrapperWidth]);

  return (
    <div className={className}>
      <div className={styles.productGalleryWrapper}>
        <div className={styles.imageWrapper} ref={ref}>
          <Image
            className={classNames(styles.mainImage, { [styles.default]: defaultImageUsed })}
            style={positionStyles}
            src={selectedImage.path}
            width={500}
            height={500}
            sizes="(max-width: 500px) 100vw, 500px"
            title={title}
            alt=''
            // white placeholder image
            blurDataURL={PlaceholderImageWhite}
            placeholder="blur"
            priority
          />
        </div>
        {images.length > 1 && (
          <div className={styles.thumbnails}>
            {images.map((data: ImageType, index: number) => (
              <button
                key={`im-${index}`}
                onClick={() => {
                  setSelectedImage(data);
                }}
                className={classNames({
                  [styles.active]: selectedImage?.id == data?.id,
                })}
              >
                <Image
                  src={data.path}
                  width={100}
                  height={100}
                  sizes="(max-width: 500px) 100vw, 500px"
                  title={title}
                  alt={title}
                  // Gray placeholder image
                  blurDataURL={PlaceholderImageGrey}
                  placeholder="blur"
                  priority
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
